import type { OpenApiPartner } from '../types/openApiPartner';

/**
 * 제휴사 화면 표시·입력 변환. 시각은 BE Instant(UTC) — 화면은 관리자 PC 시간대와 무관하게 KST 로 보인다.
 */
const KST = new Intl.DateTimeFormat('ko-KR', {
  timeZone: 'Asia/Seoul',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

const kstParts = (iso: string | null | undefined) => {
  if (!iso) return null;
  const d = new Date(iso.replace(/(\.\d{3})\d+/, '$1'));
  if (Number.isNaN(d.getTime())) return null;
  const parts = KST.formatToParts(d);
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value ?? '';
  return { date: `${part('year')}-${part('month')}-${part('day')}`, time: `${part('hour')}:${part('minute')}` };
};

/** UTC ISO → KST "YYYY-MM-DD HH:mm". 비었거나 읽을 수 없으면 null. */
export const formatKstDateTime = (iso: string | null | undefined): string | null => {
  const p = kstParts(iso);
  return p ? `${p.date} ${p.time}` : null;
};

/** UTC ISO → KST "YYYY-MM-DD"(date 입력값·기간 표시). */
export const kstDateOf = (iso: string | null | undefined): string | null => kstParts(iso)?.date ?? null;

const YMD = /^\d{4}-\d{2}-\d{2}$/;

/** 사용 시작일(KST 날짜) → 그날 00:00 KST 의 UTC ISO. 비었으면 null(제한 없음). */
export const periodFromParam = (ymd: string): string | null =>
  YMD.test(ymd) ? new Date(`${ymd}T00:00:00.000+09:00`).toISOString() : null;

/** 사용 종료일(KST 날짜) → 그날 23:59:59.999 KST 의 UTC ISO. 비었으면 null(제한 없음). */
export const periodToParam = (ymd: string): string | null =>
  YMD.test(ymd) ? new Date(`${ymd}T23:59:59.999+09:00`).toISOString() : null;

/** 사용기간 표시. 둘 다 없으면 null(호출부가 "제한 없음"). */
export const formatPeriod = (p: Pick<OpenApiPartner, 'validFrom' | 'validTo'>): string | null => {
  const from = kstDateOf(p.validFrom);
  const to = kstDateOf(p.validTo);
  if (!from && !to) return null;
  return `${from ?? ''} ~ ${to ?? ''}`.trim();
};

// ───────────────────────── 상태 ─────────────────────────

export type PartnerStatus = 'CALLABLE' | 'STOPPED' | 'OUT_OF_PERIOD' | 'NO_IP';

/**
 * 목록 상태 — 호출 가능 여부는 BE callable 이 정본이고, 불가일 때 이유만 고른다.
 * 사용 중지 > 기간 외 > IP 미등록 순(고도몰 995 > 997 > 996 와 같은 우선순위).
 */
export const partnerStatusOf = (
  p: Pick<OpenApiPartner, 'callable' | 'active' | 'validFrom' | 'validTo' | 'allowedIps'>,
  now: Date = new Date(),
): PartnerStatus => {
  if (p.callable) return 'CALLABLE';
  if (!p.active) return 'STOPPED';
  const t = now.getTime();
  const from = p.validFrom ? new Date(p.validFrom).getTime() : null;
  const to = p.validTo ? new Date(p.validTo).getTime() : null;
  if ((from != null && t < from) || (to != null && t > to)) return 'OUT_OF_PERIOD';
  if (p.allowedIps.length === 0) return 'NO_IP';
  return 'STOPPED';
};

// ───────────────────────── 허용 IP ─────────────────────────

export const MAX_ALLOWED_IPS = 20;

const IPV4 = /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}$/;
// IPv6 — 16진 그룹과 ':' 만(와일드카드 '*', 도메인 불가). 세부 형식은 BE 가 최종 판정.
const IPV6 = /^[0-9a-fA-F:]+$/;

/**
 * BE 허용 IP 규칙 — 고정 IP 또는 좁은 대역만: IPv4 /24~/32, IPv6 /64~/128.
 * /23 이하 넓은 대역·도메인·* 는 거절(BE 400). 대역의 네트워크 주소 정규화는 BE 가 한다(응답값을 그대로 표시).
 */
const isAllowedIp = (v: string): boolean => {
  const [addr = '', prefix, ...rest] = v.split('/');
  if (rest.length > 0) return false;
  const bits = prefix === undefined ? null : /^\d{1,3}$/.test(prefix) ? Number(prefix) : NaN;
  if (IPV4.test(addr)) return bits === null || (bits >= 24 && bits <= 32);
  if (addr.includes(':') && IPV6.test(addr)) return bits === null || (bits >= 64 && bits <= 128);
  return false;
};

export interface ParsedIps {
  ips: string[];
  /** 받지 않는 값(넓은 대역·도메인·* 등). */
  invalid: string[];
}

/** 여러 줄(쉼표·공백 구분도 허용) 입력 → 중복 없는 IP 목록 + 잘못된 값. */
export const parseAllowedIps = (text: string): ParsedIps => {
  const tokens = text.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean);
  const ips: string[] = [];
  const invalid: string[] = [];
  for (const tok of tokens) {
    if (!isAllowedIp(tok)) {
      if (!invalid.includes(tok)) invalid.push(tok);
    } else if (!ips.includes(tok)) {
      ips.push(tok);
    }
  }
  return { ips, invalid };
};

// ───────────────────────── 변경 이력 ─────────────────────────

export interface HistoryChange {
  field: string;
  before: string | null;
  after: string | null;
}

/** changesJson → 필드별 변경. 형식이 깨졌으면 빈 목록(이력 행은 사유만 보인다). */
export const parseChanges = (json: string | null | undefined): HistoryChange[] => {
  if (!json) return [];
  try {
    const obj = JSON.parse(json) as Record<string, { before?: unknown; after?: unknown } | null>;
    if (!obj || typeof obj !== 'object') return [];
    return Object.entries(obj).map(([field, v]) => ({
      field,
      before: v?.before == null ? null : String(v.before),
      after: v?.after == null ? null : String(v.after),
    }));
  } catch {
    return [];
  }
};
